import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/i18n/LanguageProvider";
import { Quote, ChevronLeft, ChevronRight, Star } from "lucide-react";

export const Testimonials = () => {
  const { t } = useLang();
  const items = t.testimonials.items;
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setIdx((i) => (i + 1) % items.length), 7000);
    return () => clearInterval(id);
  }, [items.length]);

  const prev = () => setIdx((i) => (i - 1 + items.length) % items.length);
  const next = () => setIdx((i) => (i + 1) % items.length);

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="relative scroll-mt-24 overflow-hidden bg-[#0B1020] py-24 text-white md:py-32"
    >
      <div className="absolute -top-32 left-1/2 h-[400px] w-[700px] -translate-x-1/2 rounded-full bg-[#7C3AED] opacity-20 blur-[140px]" />

      <div className="relative z-10 mx-auto max-w-5xl px-5 text-center md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#A5B4FC]">
            {t.testimonials.eyebrow}
          </div>
          <h2 className="mt-5 font-display text-[clamp(2rem,4.2vw,3.25rem)] font-bold leading-tight tracking-tight">
            {t.testimonials.title}
          </h2>
        </motion.div>

        <div className="relative mt-14">
          <AnimatePresence mode="wait">
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 0.5 }}
              data-testid="testimonial-slide"
              className="mx-auto max-w-3xl"
            >
              <Quote className="mx-auto h-10 w-10 text-[#7C3AED]" />
              <p className="mt-6 text-balance text-xl leading-relaxed text-slate-100 md:text-2xl">
                &ldquo;{items[idx].quote}&rdquo;
              </p>
              <div className="mt-8 flex flex-col items-center gap-1">
                <div className="flex gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-[#FACC15] text-[#FACC15]" />
                  ))}
                </div>
                <div className="mt-3 font-display text-lg font-bold">{items[idx].author}</div>
                <div className="text-sm text-slate-400">{items[idx].role}</div>
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="mt-10 flex items-center justify-center gap-3">
            <button
              onClick={prev}
              data-testid="testimonial-prev"
              aria-label="Previous"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-white/5 backdrop-blur-md transition-all hover:scale-105 hover:bg-white/10"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <div className="flex gap-1.5">
              {items.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIdx(i)}
                  aria-label={`Slide ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all ${
                    i === idx ? "w-8 bg-gradient-to-r from-[#2563EB] to-[#7C3AED]" : "w-1.5 bg-white/30"
                  }`}
                />
              ))}
            </div>
            <button
              onClick={next}
              data-testid="testimonial-next"
              aria-label="Next"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-white/5 backdrop-blur-md transition-all hover:scale-105 hover:bg-white/10"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
